// External imports
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

// Internal imports
import appConfig from "./app-config"; // Keep for now incase we go back to using the config local model
import { GamePageLayout } from "../../../components/game/GamePageLayout/GamePageLayout";
import { ChatPageContainer } from "./ChatScreen.styles";
import ChatLayoutChatLayoutWithForm from "./ChatLayoutWithForm/ChatLayoutWithForm";

// Dependency injection for chat page only
import { GetPromptDataFromLocalStorageType } from "../../../core/LocalStorage/patients/prompt";
import { UseLanguageType } from "../../../core/translations/LanguageContext";
import { GetTurnCountType } from "../../../core/LocalStorage/turnLogic/core";
import { GetDemographicsFromLocalStorageType } from "../../../core/LocalStorage/patients/demographics";

// Shared Dependency Injection across all the in-game pages
import {
    withGameDependencies,
    WithPatientProps,
} from "../../../core/sharedDependencies/withGameDependencies";
import { WithGameDependenciesProps } from "../../../core/sharedDependencies/types";

// Dependency Injection for Chat Screen Page
interface ChatScreenProps extends WithPatientProps {
    getPatientData: GetDemographicsFromLocalStorageType;
    getPromptFromLocalStorage: GetPromptDataFromLocalStorageType;
    getChat: ChatWithPatientType;
}

type CombinedProps = ChatScreenProps & WithGameDependenciesProps;

// Page Translations
import { ChatPageTranslations } from "./translations/types";
import { enTranslations } from "./translations/en";
import { deTranslations } from "./translations/de";
import { SupportedLanguage } from "../../../core/translations/supportedLanguages";
import { ChatWithPatientType } from "../../../core/ServerApiLayer/VOne/patients/getChat";

// Define the translations for the chat screen page
const PAGE_TRANSLATIONS: Record<SupportedLanguage, ChatPageTranslations> = {
    en: enTranslations,
    de: deTranslations,
};

/**
 * ChatScreen Component
 *
 * @param getPatientData
 * @param getPromptFromLocalStorage
 * @param getTurnCount
 * @param useLanguage
 * @constructor
 */
const ChatScreenBase: React.FC<CombinedProps> = ({
                                                     getPatientData,
                                                     getChat,
                                                     getPromptFromLocalStorage,
                                                     // Injected game dependencies
                                                     sidebarDependencies,
                                                     turnDependencies,
                                                     getJwt,
                                                     demographicsDependencies,
                                                     useLanguage,
                                                     translations,
                                                     refreshPage,
                                                 }) => {
    // Get the turn count and patient id
    const { id: id } = useParams<{ id: string }>();
    // @ts-ignore
    const patientId = parseInt(id);
    const turnCount = turnDependencies.getTurnCount();
    const patient = demographicsDependencies.getDemographicsFromLocalStorage(Number(patientId));

    // Get the unread notifications
    const unreadNotifications = turnDependencies.getNotifications();

    // State to store the chat history
    const [messagesHistory, setMessagesHistory] = useState<
        { role: "user" | "system"; content: string }[]
    >([]);
    const [initialPrompt, setInitialPrompt] = useState<string>("");
    const [isModelLoading, setIsModelLoading] = useState<boolean>(true);
    const [userMessageCount, setUserMessageCount] = useState(0);

    // Translations
    const { language } = useLanguage();
    const pageTranslations = PAGE_TRANSLATIONS[language];

    // Fetch the initial prompt from the backend
    useEffect(() => {
        const fetchInitialPrompt = async () => {
            try {
                const prompt = getPromptFromLocalStorage(Number(patientId), "CHATBOT");
                setInitialPrompt(prompt || "Hello! I am your patient. How can I help?");
            } catch (err) {
                console.error("Failed to fetch initial prompt:", err);
            }
        };

        fetchInitialPrompt();
        setIsModelLoading(false);
    }, []);

    // Add initial prompt to chat history for the model
    useEffect(() => {
        if (initialPrompt) {
            setMessagesHistory([{ role: "system", content: initialPrompt }]);
        }
    }, [initialPrompt]);

    // Function to handle sending messages and receiving responses
    const handleSubmit = async (userMessageObject: { message: string }) => {
        const userMessage = userMessageObject.message;
        const jwt = getJwt(); // Retrieve JWT token

        // Append user message to chat history
        setMessagesHistory((prev) => [...prev, { role: "user", content: userMessage }]);

        setUserMessageCount((prevCount) => {
            const newCount = prevCount + 1;
            if (newCount >= 5) {
                turnDependencies.updateTurns(1);
                return 0;
            }
            return newCount;
        });

        // Format chat history for the API request
        const chatPayload = {
            patient_id: patientId,
            messages: [
                ...messagesHistory, // Include previous messages
                { role: "user", content: userMessage }, // Add latest user message
            ],
            question: userMessage,
            temperature: 0.7,
            max_tokens: 1024,
        };

        try {
            //@ts-ignore
            const response = await getChat(chatPayload, jwt);

            if ("choices" in response) {
                const aiResponse = response.choices[0].message.content.trim();

                // Append AI response to chat history
                setMessagesHistory((prev) => [...prev, { role: "system", content: aiResponse }]);
            } else {
                console.error("❌ Chat API Error:", response.error);
            }
        } catch (err) {
            console.error("❌ Unexpected error in chat:", err);
        }
    };

    // Mark the chat as seen in local storage so modals is shown.
    useEffect(() => {
        localStorage.setItem(`patient_${patientId}_chat_seen`, "yes");
    }, [patientId]);


    return (
        <GamePageLayout
            turnCount={turnCount}
            unreadNotifications={unreadNotifications}
            pageTitle={pageTranslations.title}
            patient={patient}
            translations={translations}
            documentationDependencies={sidebarDependencies}
            turnDependencies={turnDependencies}
            demographicsDependencies={demographicsDependencies}
            useLanguage={useLanguage}
            refreshPage={refreshPage}
        >
            <ChatPageContainer>
                <ChatLayoutChatLayoutWithForm
                    messagesHistory={messagesHistory}
                    onSubmit={handleSubmit}
                    isDisabled={isModelLoading}
                    translations={pageTranslations}
                />
            </ChatPageContainer>
        </GamePageLayout>
    );
};

export const ChatScreen = withGameDependencies<ChatScreenProps>(ChatScreenBase);
